import { supabase } from '@/integrations/supabase/client';
import type { StoredActiveCheckinSession } from '@/lib/ba/activeShiftStorage';

const supabaseAny = supabase as any;

type CampaignReportSheetRow = {
  id: string;
  campaign_id: string;
  org_id: string | null;
  report_sheet_id: string;
  report_sheet_version_id: string | null;
  instance_name: string | null;
  required: boolean;
  required_for_checkout: boolean;
  step_kind: string | null;
  shift_id: string | null;
  store_id: string | null;
  created_at?: string | null;
};

type ReportSheetVersionRow = {
  id: string;
  status: string | null;
  report_sheet_id: string;
  definition_json?: unknown;
};

type ReportSheetRow = {
  id: string;
  title: string;
  description: string | null;
  sheet_kind: string;
  archived_at?: string | null;
  published_version_id?: string | null;
  current_draft_version_id?: string | null;
};

export type PromoterCampaignSurvey = CampaignReportSheetRow & {
  report_sheet_version: (ReportSheetVersionRow & {
    report_sheet: ReportSheetRow | null;
  }) | null;
};

type SessionScope = Pick<StoredActiveCheckinSession, 'shiftId' | 'storeId'>;

export const isSurveyRelevantToActiveSession = (
  survey: Pick<CampaignReportSheetRow, 'shift_id' | 'store_id'>,
  activeSession?: SessionScope | null,
) => {
  if (!activeSession) return true;

  const shiftMatches = !survey.shift_id || survey.shift_id === activeSession.shiftId;
  const storeMatches = !survey.store_id || survey.store_id === activeSession.storeId;

  return shiftMatches && storeMatches;
};

export const mergePromoterCampaignSurveys = (
  surveyLinks: CampaignReportSheetRow[],
  versions: ReportSheetVersionRow[],
  sheets: ReportSheetRow[],
  activeSession?: SessionScope | null,
) => {
  const versionsById = new Map(versions.map((version) => [version.id, version]));
  const sheetsById = new Map(sheets.map((sheet) => [sheet.id, sheet]));

  return surveyLinks
    .filter((survey) => isSurveyRelevantToActiveSession(survey, activeSession))
    .map((survey) => {
      const linkedSheet = sheetsById.get(survey.report_sheet_id) ?? null;
      const resolvedVersionId =
        survey.report_sheet_version_id ??
        linkedSheet?.published_version_id ??
        linkedSheet?.current_draft_version_id ??
        null;
      const version = resolvedVersionId ? versionsById.get(resolvedVersionId) ?? null : null;
      const resolvedReportSheetId = version?.report_sheet_id ?? survey.report_sheet_id;
      const sheet = sheetsById.get(resolvedReportSheetId) ?? linkedSheet ?? null;

      return {
        ...survey,
        report_sheet_version: version
          ? {
              ...version,
              report_sheet: sheet,
            }
          : null,
      };
    })
    .filter((survey) => {
      const sheet = survey.report_sheet_version?.report_sheet;
      return Boolean(sheet) && sheet?.sheet_kind === 'survey' && !sheet.archived_at;
    });
};

export const fetchPromoterCampaignSurveys = async ({
  campaignId,
  activeSession,
  includeDefinition = false,
}: {
  campaignId: string;
  activeSession?: SessionScope | null;
  includeDefinition?: boolean;
}): Promise<PromoterCampaignSurvey[]> => {
  const { data: surveyLinks, error: surveyLinksError } = await supabaseAny
    .from('campaign_report_sheets')
    .select(
      `
      id,
      campaign_id,
      org_id,
      report_sheet_id,
      report_sheet_version_id,
      instance_name,
      required,
      required_for_checkout,
      step_kind,
      shift_id,
      store_id,
      created_at
    `,
    )
    .eq('campaign_id', campaignId)
    .eq('context', 'ba')
    .order('created_at', { ascending: true });

  if (surveyLinksError) throw surveyLinksError;

  const links = ((surveyLinks ?? []) as CampaignReportSheetRow[]).filter((survey) =>
    isSurveyRelevantToActiveSession(survey, activeSession),
  );

  if (links.length === 0) return [];

  const versionIds = Array.from(
    new Set(links.map((survey) => survey.report_sheet_version_id).filter(Boolean)),
  ) as string[];
  const reportSheetIds = Array.from(
    new Set(links.map((survey) => survey.report_sheet_id).filter(Boolean)),
  ) as string[];

  const uniqueReportSheetIds = Array.from(new Set(reportSheetIds));
  let sheets: ReportSheetRow[] = [];
  if (uniqueReportSheetIds.length > 0) {
    const { data: sheetRows, error: sheetError } = await supabaseAny
      .from('report_sheets')
      .select(
        'id, title, description, sheet_kind, archived_at, published_version_id, current_draft_version_id',
      )
      .in('id', uniqueReportSheetIds);
    if (sheetError) throw sheetError;

    sheets = (sheetRows ?? []) as ReportSheetRow[];
  }

  const sheetsById = new Map(sheets.map((sheet) => [sheet.id, sheet]));
  const fallbackVersionIds = links
    .filter((survey) => !survey.report_sheet_version_id)
    .map((survey) => {
      const linkedSheet = sheetsById.get(survey.report_sheet_id);
      return linkedSheet?.published_version_id ?? linkedSheet?.current_draft_version_id ?? null;
    })
    .filter(Boolean) as string[];

  const allVersionIds = Array.from(new Set([...versionIds, ...fallbackVersionIds]));
  let versions: ReportSheetVersionRow[] = [];
  if (allVersionIds.length > 0) {
    const versionSelect = includeDefinition
      ? 'id, status, report_sheet_id, definition_json'
      : 'id, status, report_sheet_id';

    const { data: versionRows, error: versionError } = await supabaseAny
      .from('report_sheet_versions')
      .select(versionSelect)
      .in('id', allVersionIds);
    if (versionError) throw versionError;

    versions = (versionRows ?? []) as ReportSheetVersionRow[];
  }

  return mergePromoterCampaignSurveys(links, versions, sheets, activeSession);
};
